import { UMB_ENTITY_CONTEXT } from './entity.context-token.js';
import type { UmbEntityUnique } from './types.js';
import { UmbContextBase } from '@umbraco-cms/backoffice/class-api';
import type { UmbControllerHost } from '@umbraco-cms/backoffice/controller-api';
import { UmbStringState } from '@umbraco-cms/backoffice/observable-api';

/**
 * Provides the entity context
 * @class UmbEntityContext
 * @augments {UmbContextBase}
 */
export class UmbEntityContext extends UmbContextBase {
	#entityType = new UmbStringState<string | undefined>(undefined);
	public readonly entityType = this.#entityType.asObservable();

	// TODO v19: Accept undefined as initial value to align with workspace contexts that start unloaded.
	#unique = new UmbStringState<UmbEntityUnique>(null);
	public readonly unique = this.#unique.asObservable();

	/**
	 * Creates an instance of UmbEntityContext.
	 * @param {UmbControllerHost} host - The controller host for this controller to be appended to
	 * @memberof UmbEntityContext
	 */
	constructor(host: UmbControllerHost) {
		super(host, UMB_ENTITY_CONTEXT);
	}

	/**
	 * Set the entity type
	 * @param {string | undefined} entityType - The entity type
	 * @memberof UmbEntityContext
	 */
	setEntityType(entityType: string | undefined) {
		this.#entityType.setValue(entityType);
	}

	/**
	 * Get the entity type
	 * @returns {string | undefined} The entity type
	 * @memberof UmbEntityContext
	 */
	getEntityType(): string | undefined {
		return this.#entityType.getValue();
	}

	/**
	 * Set the unique
	 * @param {string | null} unique - The unique identifier
	 * @memberof UmbEntityContext
	 */
	setUnique(unique: string | null) {
		this.#unique.setValue(unique);
	}

	/**
	 * Get the unique
	 * @returns {string | null} The unique identifier
	 * @memberof UmbEntityContext
	 */
	getUnique() {
		return this.#unique.getValue();
	}
}
